import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
	Autocomplete, Box, Button, Chip, IconButton, Link, ListItemText, MenuItem, Popover, Select, Skeleton, Stack, Table, TableBody,
	TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import { dsColorNew } from '@palette'
import { alpha } from '@mui/material/styles'
import {
	AppDialog, AppDialogHeader, AppDialogBody, AppDialogFooter, AppDialogCancel, AppBulkEditAction, AppPillToggle, AppNote,
	secondaryButtonSx,
} from '@components'
import useToast from '@hooks/useToast'
import { DS_REPRICING_ROUTES } from '@constants/routes'
import { dialogBodySx } from 'Pages/DsRepricing/@components/dialogBodySx'
import { setupScopeSx } from 'Pages/DsRepricing/Setup/@components/setupScopeSx'
import { Add as AddIcon, DeleteOutline as DeleteIcon, EditOutlined as EditIcon, LinkOutlined as LinkIcon, ArrowRightAlt as ArrowIcon, SearchOutlined as SearchIcon, FilterAltOutlined as FilterIcon } from 'Pages/DsRepricing/@components/icons'

import { useCategories } from 'Pages/DsRepricing/Setup/@services/useCategories'
import { useOfficeScope } from 'Pages/DsRepricing/Setup/@services/useOfficeScope'
import { usePricelistCatalog } from 'Pages/DsRepricing/Setup/@services/pricelists'
import { ProductsProvider, useProductsCatalog } from 'Pages/DsRepricing/Setup/@services/useProducts'
import { RateShopsProvider, useTenantRateShops } from 'Pages/DsRepricing/Page/@hooks/useRateShops'
import {
	tenantBrackets, valueLabel, autoName, pickerOptions, staleValues, appliesSummary, fitSentence, deleteSentence, strategyStatus,
} from 'Pages/DsRepricing/Setup/@services/tethers'
import { uid } from 'Pages/DsRepricing/Setup/@mocks'
import { useSetupCollection } from 'Pages/DsRepricing/Setup/@services'
import {
	Surface, SurfaceHeader, EmptyState, LoadingState, HelpNote,
} from 'Pages/DsRepricing/Setup/@components/Surface'
import { inputSx, rowHoverSx, cellPadSx, headCellSx } from 'Pages/DsRepricing/Setup/@components/formStyles'
import UnsavedChip from 'Pages/DsRepricing/Setup/@components/UnsavedChip'

// Persistence: @services/useSetupCollection (entity 'tethers').

/**
 * Tethering — inheritance rules between values of a same dimension.
 *
 * When the parent is repriced to X, EACH child is inferred as X · (1+%)
 * or X + € — no independent computation, just a fixed spread. One
 * parent can fan out to multiple children that all share the same
 * delta, and a value may head several tethers, each with its own delta
 * ("7 → 4" −10 % and "7 → 8–9" +4 %). A child belongs to one tether and
 * is never a parent in its dimension: no chains (CU-869fkr2yp, brief
 * criterion 13).
 *
 * A tether acts only in the strategies that pick it, and only where it
 * fits them (the engine's rule, criterion 9): the "Applies to" column
 * reads that rule from the rate-shops estimate (`data.tethers`).
 *
 * Dimensions: LOR (the brackets of the tenant's read pricelists, stored
 * by their reference LOR), ACRISS (SIPP), Office. Category tethers are
 * not applied by the engine yet: existing ones open, edit and delete,
 * new ones cannot be created (criterion 3).
 */

// v1 → v2 migration: single `child` becomes `children: [child]`.
function migrateTether(t) {
	if (!t) return t
	if (Array.isArray(t.children)) return t
	if (t.child) {
		const { child, ...rest } = t
		return { ...rest, children: [child] }
	}
	return { ...t, children: [] }
}

const DIMENSIONS = [
	{ code: 'lor',      label: 'LOR',      short: 'LOR',      mono: false,
	  palette: { bg: dsColorNew.pillActiveBg, fg: dsColorNew.appBlue, border: dsColorNew.noteInfoBorder } },
	{ code: 'category', label: 'Category', short: 'CATEGORY', mono: false,
	  palette: { bg: dsColorNew.noteInfoBg, fg: dsColorNew.noteInfo, border: dsColorNew.noteInfoBorder } },
	{ code: 'acriss',   label: 'ACRISS',   short: 'ACRISS',   mono: true,
	  palette: { bg: dsColorNew.noteAmberBg, fg: dsColorNew.noteAmber, border: dsColorNew.noteAmberBorder } },
	{ code: 'office',   label: 'Office',   short: 'OFFICE',   mono: true,
	  palette: { bg: dsColorNew.successBg, fg: dsColorNew.successText, border: dsColorNew.successBar } },
]
const DIM_BY_CODE = Object.fromEntries(DIMENSIONS.map(d => [d.code, d]))

/**
 * The values a tether dimension can take, from the tenant's own data.
 *
 * `ACRISS_TREE`, `ALL_SIPPS` and `OFFICES` were hand-written for the
 * prototype: the offices were six Canarian airports the tenant does
 * not have (its office is `A1`) and the SIPPs missed eight codes the
 * engine is repricing. A tether built from either could never match
 * (Curro, 2026-09-28: "nada mock ya").
 *
 * LOR is no longer a fixed list (CU-869fkr2yp, criterion 12): the
 * brackets of every product's read pricelists, one per reference LOR,
 * with the wizard's label (`tenantBrackets`). `lorState` says whether
 * they are loading, failed or there are none (criterion 21).
 */
function useDimensionCatalog() {
	const { tree, allSipps } = useCategories()
	const { offices } = useOfficeScope()
	const { items: pricelists, loading: plLoading, error: plError, reload: plReload } = usePricelistCatalog()
	const { items: products, loading: prLoading, error: prError, reload: prReload } = useProductsCatalog()
	return useMemo(() => {
		const brackets = tenantBrackets(products, pricelists)
		const ctx = { brackets, categories: tree }
		const lorValues = brackets.map(b => ({ code: b.code, label: b.label }))
		const values = (dim) => {
			switch (dim) {
				case 'lor':      return lorValues
				case 'category': return tree.map(m => ({ code: m.code, label: m.name }))
				case 'acriss':   return allSipps.map(s => ({ code: s, label: s }))
				case 'office':   return offices.map(o => ({
					code: o.code,
					label: o.name && o.name !== o.code ? `${o.code} · ${o.name}` : o.code,
				}))
				default:         return []
			}
		}
		const label = (dim, code) =>
			dim === 'lor' || dim === 'category' ? valueLabel(dim, code, ctx)
			  : values(dim).find(v => v.code === code)?.label || code
		// Compact form for a chip: no "· name" tail.
		const short = (dim, code) => valueLabel(dim, code, ctx)
		const loading = !!(plLoading || prLoading)
		const error = plError || prError || null
		const lorState = {
			loading, error,
			empty: !loading && !error && brackets.length === 0,
			ready: !loading && !error && brackets.length > 0,
			reload: () => { plReload?.(); prReload?.() },
		}
		return { values, label, short, brackets, lorState }
	}, [tree, allSipps, offices, pricelists, products, plLoading, prLoading, plError, prError])
}

function formatDelta(d) {
	if (!d || d.v == null) return ''
	const sign = d.v > 0 ? '+' : ''
	return d.k === 'pct' ? `${sign}${d.v}%` : `${sign}${d.v}€`
}

const DEFAULT_TETHERS = [
	{ id: uid(), dimension: 'lor',      parent: '1',       children: ['3', '5'],
	  delta: { k: 'pct', v: 5 },  name_custom: false, name: '' },
	{ id: uid(), dimension: 'lor',      parent: '7',       children: ['14', '21'],
	  delta: { k: 'pct', v: -8 }, name_custom: false, name: '' },
	{ id: uid(), dimension: 'category', parent: 'compact', children: ['standard'],
	  delta: { k: 'abs', v: 8 },  name_custom: false, name: '' },
	{ id: uid(), dimension: 'acriss',   parent: 'MDMR',    children: ['MDAR'],
	  delta: { k: 'pct', v: 12 }, name_custom: true,  name: 'Manual→Auto (Mini)' },
	{ id: uid(), dimension: 'office',   parent: 'LPA-AP',  children: ['LPA-CEN'],
	  delta: { k: 'abs', v: 3 },  name_custom: false, name: '' },
].map(t => ({ ...t, name: t.name_custom ? t.name : autoName(t) }))

/**
 * The products (for the LOR brackets) and the engine's rate-shops answer
 * (for "Applies to") are read once for the tab; neither blocks the table.
 */
export default function TetheringTab() {
	return (
		<ProductsProvider>
			<RateShopsProvider>
				<TetheringTable/>
			</RateShopsProvider>
		</ProductsProvider>
	)
}

function TetheringTable() {
	const cat = useDimensionCatalog()
	const shops = useTenantRateShops()
	const { showToast } = useToast()
	const { items: products } = useProductsCatalog()
	// Persisted through webback (or the localStorage mock behind the flag).
	const [tethers, setTethers, sync] = useSetupCollection('tethers', {
		mockDefault: DEFAULT_TETHERS, normalise: migrateTether,
	})
	const [dimFilter, setDimFilter] = useState('*')
	const [search, setSearch]       = useState('')
	const [dialogState, setDialogState] = useState(null)  // {mode:'add'|'edit', tether}

	// A tether saved, edited or deleted changes where it applies: the
	// count is reread once the collection has finished saving (webback
	// drops its cache on every tether change).
	const wasSaving = useRef(false)
	useEffect(() => {
		if (wasSaving.current && !sync.saving) shops.reload?.({ quiet: true })
		wasSaving.current = sync.saving
	}, [sync.saving])   // eslint-disable-line react-hooks/exhaustive-deps

	// The engine's fit per strategy, or null while loading, on error, or
	// with an engine that does not send it yet (criterion 21: "—").
	const fitsByStrategy = shops.error ? null : shops.data?.tethers || null
	const countState = shops.loading && !shops.data ? 'loading' : fitsByStrategy ? 'ready' : 'error'
	const productName = (id) => (products || []).find(p => String(p.id) === String(id))?.name || null

	function upsertTether(next) {
		setTethers(prev => {
			const idx = prev.findIndex(t => t.id === next.id)
			if (idx < 0) return [next, ...prev]
			const patched = [...prev]
			patched[idx] = next
			return patched
		})
	}
	function deleteTether(id) {
		setTethers(prev => prev.filter(t => t.id !== id))
	}
	// Criterion 20: deleting asks, and says how many strategies stop using it.
	function confirmDelete(t) {
		const summary = t.dimension === 'category' ? null : appliesSummary(t.id, t.dimension, fitsByStrategy)
		showToast.confirm(deleteSentence(t, summary ? summary.count : null), {
			title: 'Delete this tether?', tone: 'danger', submitText: 'Delete tether',
			onSubmit: () => deleteTether(t.id),
		})
	}

	const visible = useMemo(() => tethers.filter(t => {
		if (dimFilter !== '*' && t.dimension !== dimFilter) return false
		if (search) {
			const hay = [t.name, t.parent,
			             ...(t.children || []),
			             cat.label(t.dimension, t.parent),
			             ...(t.children || []).map(c => cat.label(t.dimension, c))]
				.filter(Boolean).join(' ').toLowerCase()
			if (!hay.includes(search.toLowerCase())) return false
		}
		return true
	}), [tethers, dimFilter, search, cat])

	return (
		<Surface padded={false}>
			<Box sx={{ p: 2.5 }}>
				<SurfaceHeader
					// The card's notes, with its description, behind the ⓘ of its title
					info={<>
						<Box>
						<strong>How strategies use tethers:</strong> a tether acts only
						in the strategies that pick it (step 5, "Then · Tethering"),
						and only where it fits them: its parent and at least one child
						inside the strategy's product brackets and Rental lengths,
						offices or cars. When the engine reprices the parent, each
						child that fits follows it with the tether's spread, on the
						same product row, pickup date and car — and the strategy's own
						price for that child gives way to the tether.
						<br/><br/>
						<strong>Values:</strong> a value may head several tethers, each
						with its own delta (<em>7 → 4</em> and <em>7 → 8–9</em>). A child
						belongs to one tether and is never a parent: no chains. A value
						that cannot be picked shows disabled with the tether that uses
						it. Rental lengths are the brackets of your read pricelists.
						<br/><br/>
						<strong>Application order:</strong> ACRISS → Rental length →
						Office; the children of one dimension feed the next. Category
						tethers are not applied by the engine yet.
						</Box>
					</>}
					icon="link"
					title="Tethering (Reglas de herencia)"
					subtitle={<>When a value is repriced, another value inherits the
					result with a <strong>fixed spread</strong> (%, €), in the
					strategies that pick the tether. A value may head several tethers;
					a child belongs to one and is never a parent.</>}
					actions={
						<AppBulkEditAction startIcon={<AddIcon/>}
						                   onClick={() => setDialogState({ mode: 'add' })}>
							Add tether
						</AppBulkEditAction>
					}
					sx={{ mb: 2 }}
				/>

				{/* The table's filters on the card itself, as the fields of the app (no grey box) */}
				<Box sx={{ pb: 2, mb: 2, borderBottom: `1px solid ${dsColorNew.grayBorder}` }}>
					<Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
						<Select size="small" value={dimFilter}
						        onChange={(e) => setDimFilter(e.target.value)}
						        sx={{ minWidth: 180 }}>
							<MenuItem value="*"><em>All dimensions</em></MenuItem>
							{DIMENSIONS.map(d => (
								<MenuItem key={d.code} value={d.code}>{d.label}</MenuItem>
							))}
						</Select>
						<TextField size="small"
						           placeholder="Search by name, value…"
						           value={search}
						           onChange={(e) => setSearch(e.target.value)}
						           InputProps={{ startAdornment: (
						               <SearchIcon sx={{ fontSize: 14, color: dsColorNew.textSubtle, mr: 1 }}/>
						           ) }}
						           sx={{ minWidth: 280 }}/>
						<Box sx={{ flex: 1 }}/>
						<Typography sx={{ fontSize: 12.5, color: dsColorNew.textMuted }}>
							{visible.length === tethers.length
							  ? `${tethers.length} tether${tethers.length === 1 ? '' : 's'}`
							  : `${visible.length} of ${tethers.length} shown`}
						</Typography>
						{(dimFilter !== '*' || search) && (
							<Button variant="outlined" onClick={() => { setDimFilter('*'); setSearch('') }}
							        sx={{ ...secondaryButtonSx, height: 36 }}>
								Clear
							</Button>
						)}
					</Stack>
				</Box>

				{sync.loading ? (
					<LoadingState what="tethers"/>
				) : visible.length === 0 ? (
					<EmptyState
						icon={<LinkIcon/>}
						title={tethers.length === 0 ? 'No tethers yet' : 'No tethers match these filters'}
						description={tethers.length === 0
						  ? 'Create a first tether to have one value inherit the price of another with a fixed spread.'
						  : 'Loosen the filter or clear it above.'}
						action={
							<Button variant="outlined" startIcon={<AddIcon/>}
							        onClick={() => setDialogState({ mode: 'add' })}
							        sx={{ ...secondaryButtonSx, height: 36 }}>
								Add tether
							</Button>
						}
					/>
				) : (
					<TableContainer sx={{ border: '1px solid', borderColor: dsColorNew.grayBorder,
					                      borderRadius: 1, overflow: 'auto' }}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<HeadCell width={240}>Name</HeadCell>
									<HeadCell width={110}>Dimension</HeadCell>
									<HeadCell>Parent → Child</HeadCell>
									<HeadCell align="right" width={100}>Delta</HeadCell>
									<HeadCell width={210}>Applies to</HeadCell>
									<HeadCell width={90}></HeadCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{visible.map(t => (
									<TableRow key={t.id} sx={{ ...rowHoverSx }}>
										<TableCell sx={cellPadSx}>
											<Typography sx={{
												fontSize: 13,
												fontWeight: t.name_custom ? 700 : 500,
												fontStyle: t.name_custom ? 'normal' : 'italic',
												color: t.name_custom ? dsColorNew.textTitle : dsColorNew.textMuted,
											}}>
												{t.name}
											</Typography>
										</TableCell>
										<TableCell sx={cellPadSx}>
											<DimensionChip dim={t.dimension}/>
										</TableCell>
										<TableCell sx={cellPadSx}>
											<TetherArrow tether={t}
											             stale={cat.lorState.ready || cat.lorState.empty ? staleValues(t, cat.brackets) : []}/>
										</TableCell>
										<TableCell align="right" sx={cellPadSx}>
											<DeltaChip delta={t.delta}/>
										</TableCell>
										<TableCell sx={cellPadSx}>
											<AppliesCell tether={t} state={countState}
											             fitsByStrategy={fitsByStrategy}
											             productName={productName}
											             valueLabel={(v) => cat.short(t.dimension, v)}/>
										</TableCell>
										<TableCell sx={cellPadSx}>
											<Stack direction="row" gap={0.25} alignItems="center" justifyContent="flex-end">
												<UnsavedChip refused={sync.refused?.get(t.id)}
												             stalled={sync.stalled?.has(t.id)}
												             reason={sync.held?.has(t.id)
												               ? 'Pick at least one value that inherits and it saves itself.' : null}/>
												<IconButton size="small"
												            title="Edit tether"
												            onClick={() => setDialogState({ mode: 'edit', tether: t })}
												            sx={{ color: dsColorNew.textMuted,
												                  '&:hover': { color: dsColorNew.appBlue,
												                               bgcolor: dsColorNew.pillActiveBg } }}>
													<EditIcon fontSize="small"/>
												</IconButton>
												<IconButton size="small"
												            title="Delete tether"
												            onClick={() => confirmDelete(t)}
												            sx={{ color: dsColorNew.textMuted,
												                  '&:hover': { color: dsColorNew.dangerRed,
												                               bgcolor: dsColorNew.dangerBg } }}>
													<DeleteIcon fontSize="small"/>
												</IconButton>
											</Stack>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				)}

			</Box>

			<TetherDialog state={dialogState}
			              tethers={tethers}
			              onClose={() => setDialogState(null)}
			              onSave={(t) => { upsertTether(t); setDialogState(null) }}/>
		</Surface>
	)
}

const POPOVER_MAX = 8
const strategyLink = (id) => `${DS_REPRICING_ROUTES.CONFIGURATION_PATH}?rule=${encodeURIComponent(id)}`

/**
 * "N strategies ▾" — where the tether applies, by the engine's rule
 * (criterion 15). The popover lists those strategies (status, link that
 * opens it) and the ones that picked it where it does nothing, with why.
 * Loading: a short bar; no data: "—". Never blocks the table.
 */
function AppliesCell({ tether, state, fitsByStrategy, productName, valueLabel: label }) {
	const [anchor, setAnchor] = useState(null)
	if (tether.dimension === 'category') {
		return <Typography sx={{ fontSize: 12.5, color: dsColorNew.textSubtle, fontStyle: 'italic' }}>not applied yet</Typography>
	}
	if (state === 'loading') return <Skeleton variant="rounded" width={90} height={14}/>
	const summary = state === 'ready' ? appliesSummary(tether.id, tether.dimension, fitsByStrategy) : null
	if (!summary) {
		return (
			<Typography title="Could not count the strategies; the tether still works"
			            sx={{ fontSize: 12.5, color: dsColorNew.textSubtle }}>—</Typography>
		)
	}
	const clickable = summary.count > 0 || summary.notApplying.length > 0
	if (!clickable) return <Typography sx={{ fontSize: 12.5, color: dsColorNew.textSubtle }}>{summary.label}</Typography>
	const shown = summary.applies.slice(0, POPOVER_MAX)
	const more = summary.applies.length - shown.length
	const why = (n) => {
		const s = fitSentence(n.fit, { productName: productName(fitsByStrategy?.[n.strategy.id]?.product_id), value: label })
		return s ? s.replace(/^Doesn't apply here: /, '') : 'it does not fit this strategy'
	}
	return (
		<>
			<Button size="small" variant="text" onClick={(e) => setAnchor(e.currentTarget)}
			        sx={{ fontSize: 12.5, fontWeight: 600, textTransform: 'none', px: 1, minWidth: 0,
			              color: summary.count ? dsColorNew.appBlue : dsColorNew.textMuted }}>
				{summary.label} ▾
			</Button>
			<Popover open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)}
			         anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
			         transformOrigin={{ vertical: 'top', horizontal: 'left' }}
			         slotProps={{ paper: { sx: {
			             mt: 0.75, maxWidth: 480, borderRadius: '12px', border: `1px solid ${dsColorNew.grayBorder}`,
			             boxShadow: `0 16px 40px ${alpha(dsColorNew.navy, 0.16)}`, backgroundColor: dsColorNew.white,
			         } } }}>
				<Box sx={{ px: 2, pt: 1.5, pb: 1.75, fontSize: 13, color: dsColorNew.textDark, display: 'flex', flexDirection: 'column', gap: 1 }}>
					<Typography sx={{ fontSize: 14, fontWeight: 600, color: dsColorNew.textTitle }}>
						{tether.name} applies to {summary.count ? summary.label : 'no strategy'}
					</Typography>
					{shown.map(s => (
						<Stack key={s.id} direction="row" gap={1} alignItems="center">
							<Typography sx={{ fontSize: 13, flex: 1 }}>{s.name}</Typography>
							<Chip size="small" label={strategyStatus(s.status).label}
							      sx={{ height: 20, fontSize: 11, fontWeight: 700,
							            bgcolor: strategyStatus(s.status).on ? dsColorNew.successBg : dsColorNew.pillTrackBg,
							            color: strategyStatus(s.status).on ? dsColorNew.successText : dsColorNew.textMuted }}/>
							<Link href={strategyLink(s.id)} target="_blank" rel="noopener"
							      sx={{ fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap' }}>
								Open ↗
							</Link>
						</Stack>
					))}
					{more > 0 && <Typography sx={{ fontSize: 12.5, color: dsColorNew.textMuted }}>and {more} more</Typography>}
					{summary.notApplying.length > 0 && (
						<>
							<Typography sx={{ fontSize: 12, fontWeight: 700, color: dsColorNew.textMuted, mt: 0.5 }}>
								Picked, but does nothing in:
							</Typography>
							{summary.notApplying.map(n => (
								<Typography key={n.strategy.id} sx={{ fontSize: 12.5, color: dsColorNew.noteAmber }}>
									<Link href={strategyLink(n.strategy.id)} target="_blank" rel="noopener"
									      sx={{ fontWeight: 600 }}>{n.strategy.name}</Link>
									{' — '}{why(n)}.
								</Typography>
							))}
						</>
					)}
				</Box>
			</Popover>
		</>
	)
}

// ---------------------------------------------------------------------------
// Dialog — Add / Edit tether
// ---------------------------------------------------------------------------
function TetherDialog({ state, tethers, onClose, onSave }) {
	const cat = useDimensionCatalog()
	const isOpen = !!state
	const isEdit = state?.mode === 'edit'
	const editingId = isEdit ? state.tether.id : null

	const [dimension, setDimension]       = useState('lor')
	const [parentCode, setParentCode]     = useState('')
	const [childCodes, setChildCodes]     = useState([])
	const [delta, setDelta]               = useState({ k: 'pct', v: 5 })
	// Direction is held apart from the amount. `delta.v` stays the signed
	// number that goes on the wire, but a sign derived from it alone is
	// lost the moment the amount is 0 — you would press "−", the field
	// would still read 0, and the toggle would spring back to "+".
	const [down, setDown]                 = useState(false)
	const [name, setName]                 = useState('')
	const [nameCustom, setNameCustom]     = useState(false)

	// Seed / reset when the dialog opens or the target changes.
	useEffect(() => {
		if (!isOpen) return
		if (isEdit) {
			const t = state.tether
			setDimension(t.dimension)
			setParentCode(t.parent)
			setChildCodes(Array.isArray(t.children) ? t.children : (t.child ? [t.child] : []))
			setDelta(t.delta || { k: 'pct', v: 0 })
			setDown((t.delta?.v ?? 0) < 0)
			setName(t.name || '')
			setNameCustom(!!t.name_custom)
		} else {
			setDimension('lor')
			setParentCode('')
			setChildCodes([])
			setDelta({ k: 'pct', v: 5 })
			setDown(false)
			setName('')
			setNameCustom(false)
		}
	}, [isOpen, editingId])

	// The only writer of `delta.v`: whichever half the operator touched,
	// the wire value is rebuilt from both.
	const setSigned = (isDown, magnitude) => {
		setDown(isDown)
		setDelta(d => ({ ...d, v: (isDown ? -1 : 1) * Math.abs(magnitude || 0) }))
	}

	// Every value stays in the list: one that cannot be picked is disabled
	// with the tether that uses it, a parent elsewhere carries "also parent
	// of …", and the edited tether's own values — even one that is no
	// bracket any more — stay (criteria 13-14). The value picked on the
	// other side of this tether is left out of each list.
	const allValues = cat.values(dimension)
	const parentOptions = useMemo(() =>
		pickerOptions(tethers, dimension, 'parent', editingId, allValues).filter(o => !childCodes.includes(o.code)),
		[tethers, dimension, editingId, allValues, childCodes])
	const childOptions = useMemo(() =>
		pickerOptions(tethers, dimension, 'child', editingId, allValues).filter(o => o.code !== parentCode),
		[tethers, dimension, editingId, allValues, parentCode])
	const blocked = (opts, code) => !!opts.find(o => o.code === code)?.disabled

	// LOR needs the brackets: loading, failed or none → no LOR tether is
	// saved (criterion 21); the other dimensions are as before.
	const lor = cat.lorState
	const lorBlocked = dimension === 'lor' && !lor.ready
	// Category is not applied by the engine yet (criterion 3): no new one,
	// an existing one still edits.
	const categoryAllowed = isEdit && state.tether.dimension === 'category'
	const stale = dimension === 'lor' && lor.ready
		? staleValues({ dimension, parent: parentCode, children: childCodes }, cat.brackets) : []

	// Auto-derive name until the user takes ownership.
	useEffect(() => {
		if (nameCustom) return
		if (!parentCode || childCodes.length === 0) { setName(''); return }
		setName(autoName({ dimension, parent: parentCode, children: childCodes }, cat.short))
	}, [dimension, parentCode, childCodes, nameCustom, cat])

	const isValid = parentCode && childCodes.length > 0
	                && !childCodes.includes(parentCode)
	                && !blocked(parentOptions, parentCode)
	                && !childCodes.some(c => blocked(childOptions, c))
	                && !lorBlocked
	                && (dimension !== 'category' || categoryAllowed)
	                && Number.isFinite(delta.v)

	function commit() {
		if (!isValid) return
		const derivedName = autoName({ dimension, parent: parentCode, children: childCodes }, cat.short)
		const t = {
			id: isEdit ? state.tether.id : uid(),
			dimension,
			parent: parentCode,
			children: [...childCodes],
			delta,
			name: nameCustom ? (name.trim() || derivedName) : derivedName,
			name_custom: nameCustom,
		}
		onSave(t)
	}

	if (!isOpen) return null

	// Preview line — grounds the abstract math in a concrete sentence.
	const derivedFormula = delta.k === 'pct'
		? `X × ${(1 + (delta.v || 0) / 100).toFixed(3)}€`
		: `X ${delta.v >= 0 ? '+' : '−'} ${Math.abs(delta.v || 0)}€`
	const childList = childCodes.map(c => cat.short(dimension, c))
	const preview = (parentCode && childCodes.length > 0) ? (
		<>
			When <strong>{cat.short(dimension, parentCode)}</strong> is
			repriced to <strong>X€</strong>,{' '}
			{childList.length === 1 ? (
				<><strong>{childList[0]}</strong>{' '}will be</>
			) : (
				<>
					{childList.slice(0, -1).map((c, i) => (
						<React.Fragment key={i}>
							{i > 0 && ', '}<strong>{c}</strong>
						</React.Fragment>
					))}
					{' '}and{' '}<strong>{childList[childList.length - 1]}</strong>{' '}
					will each be
				</>
			)}
			{' '}pushed to <strong>{derivedFormula}</strong> automatically
			{stale.includes(parentCode)
			  ? <> — but <strong>{parentCode}</strong> is not a bracket, so nothing is repriced as {parentCode} and this tether does nothing.</>
			  : '.'}
		</>
	) : (
		<em style={{ color: dsColorNew.textMuted }}>
			Pick a parent and at least one child to see the preview.
		</em>
	)

	return (
		<AppDialog open={isOpen} onClose={onClose} maxWidth="sm" paperSx={setupScopeSx}>
			<AppDialogHeader icon="link" title={isEdit ? 'Edit tether' : 'Add tether'} onClose={onClose}/>
			<AppDialogBody sx={dialogBodySx}>
				<Stack gap={2}>
					<Box>
						<Label>Dimension</Label>
						<Select size="small" fullWidth value={dimension}
						        onChange={(e) => {
						            setDimension(e.target.value)
						            setParentCode('')
						            // `setChildCodes([])`, plural: the singular
						            // setter is a leftover from when a tether had
						            // one child, and changing the dimension threw
						            // `ReferenceError` instead of clearing it.
						            setChildCodes([])
						        }}
						        sx={{ bgcolor: dsColorNew.white, ...inputSx }}>
							{DIMENSIONS.map(d => {
								const off = d.code === 'category' && !categoryAllowed
								return (
									<MenuItem key={d.code} value={d.code} disabled={off}>
										{off
										  ? <ListItemText primary={d.label} secondary="not applied by the engine yet — no price would move"
										                  primaryTypographyProps={{ fontSize: 14 }}
										                  secondaryTypographyProps={{ fontSize: 12 }}/>
										  : d.label}
									</MenuItem>
								)
							})}
						</Select>
						{dimension === 'category' && (
							<Typography sx={{ fontSize: 12, color: dsColorNew.noteAmber, mt: 0.5 }}>
								Category tethers are not applied by the engine yet: this one can be edited or deleted, it moves no price.
							</Typography>
						)}
					</Box>

					{dimension === 'lor' && lor.error && (
						<AppNote severity="error">
							Could not load the brackets of your read pricelists, so a rental-length tether cannot be saved now.{' '}
							<Link component="button" onClick={lor.reload} sx={{ fontWeight: 600, verticalAlign: 'baseline' }}>Try again</Link>
						</AppNote>
					)}
					{dimension === 'lor' && lor.empty && (
						<AppNote severity="warning">
							No product row has brackets saved —{' '}
							<Link href="?tab=products" sx={{ fontWeight: 600 }}>open Setup › Products</Link> and save the product.
						</AppNote>
					)}

					<Stack direction="row" gap={1.5} alignItems="flex-start">
						<Box sx={{ flex: 1 }}>
							<Label>Parent</Label>
							<ValuePicker options={parentOptions}
							             value={parentCode}
							             dim={dimension}
							             loading={dimension === 'lor' && lor.loading}
							             stale={stale.includes(parentCode)}
							             onChange={(v) => setParentCode(v)}
							             placeholder="Select parent"/>
						</Box>
						<Box sx={{ display: 'flex', alignItems: 'center', height: 40, mt: 2.5,
						           color: dsColorNew.textMuted }}>
							<ArrowIcon/>
						</Box>
						<Box sx={{ flex: 1.4 }}>
							<Label>Children (one or more, same delta)</Label>
							<ChildrenPicker options={childOptions}
							                value={childCodes}
							                dim={dimension}
							                loading={dimension === 'lor' && lor.loading}
							                stale={stale.filter(c => childCodes.includes(c))}
							                onChange={setChildCodes}
							                placeholder="Add one or more children…"/>
						</Box>
					</Stack>

					<Box>
						<Label>Delta</Label>
						<Stack direction="row" gap={0.75} alignItems="center">
							<DirectionToggle value={down ? 'down' : 'up'}
							                 onChange={(d) => setSigned(d === 'down', delta.v)}/>
							<TextField type="number" size="small"
							           value={delta.v == null ? '' : Math.abs(delta.v)}
							           onChange={(e) => {
							               const raw = e.target.value
							               if (raw === '') return setSigned(down, 0)
							               const n = Number(raw)
							               if (!Number.isFinite(n)) return
							               // A pasted "-8" sets the direction instead of
							               // being thrown away.
							               setSigned(n < 0 ? true : down, n)
							           }}
							           onKeyDown={(e) => {
							               // The reason a negative delta could not be typed:
							               // a number input reports a lone "-" as an empty
							               // string, and the old handler read that as 0, so
							               // the key did nothing at all. Now it does the
							               // thing it looks like it does.
							               if (e.ctrlKey || e.metaKey || e.altKey) return
							               if (e.key !== '-' && e.key !== '+') return
							               e.preventDefault()
							               setSigned(e.key === '-', delta.v)
							           }}
							           inputProps={{ min: 0,
							                         style: { textAlign: 'right', fontWeight: 700,
							                                  fontSize: 14, width: 80 } }}
							           sx={{ bgcolor: dsColorNew.white, ...inputSx,
							                 '& .MuiOutlinedInput-root': { height: 40 } }}/>
							<UnitToggle value={delta.k}
							            onChange={(k) => setDelta({ ...delta, k })}/>
							<Chip label={formatDelta(delta) || '—'} size="small"
							      sx={{ height: 32, fontSize: 13, fontWeight: 700,
							            minWidth: 70, borderRadius: 1,
							            bgcolor: delta.v > 0 ? dsColorNew.successBg
							                   : delta.v < 0 ? dsColorNew.dangerBg : dsColorNew.pillTrackBg,
							            color:   delta.v > 0 ? dsColorNew.successText
							                   : delta.v < 0 ? dsColorNew.dangerRed : dsColorNew.textMuted,
							            border: '1px solid',
							            borderColor: delta.v > 0 ? alpha(dsColorNew.successBar, 0.4)
							                       : delta.v < 0 ? dsColorNew.dangerBorder : dsColorNew.grayBorder }}/>
						</Stack>
					</Box>

					<Box>
						<Label>Name</Label>
						<Stack direction="row" gap={0.75}>
							<TextField size="small" fullWidth
							           value={name}
							           onChange={(e) => { setName(e.target.value); setNameCustom(true) }}
							           inputProps={{ style: { fontWeight: nameCustom ? 700 : 500,
							                                  fontStyle: nameCustom ? 'normal' : 'italic' } }}
							           sx={{ bgcolor: dsColorNew.white, ...inputSx }}/>
							{nameCustom && (
								<Button size="small" variant="text"
								        onClick={() => setNameCustom(false)}
								        sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
									Reset to auto
								</Button>
							)}
						</Stack>
						<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, mt: 0.5 }}>
							Auto-derived from parent → child. Edit to override.
						</Typography>
					</Box>

					<Box sx={{ p: 1.25, bgcolor: dsColorNew.noteInfoBg, border: `1px solid ${dsColorNew.noteInfoBorder}`,
					           borderRadius: 1 }}>
						<Typography sx={{ fontSize: 11, fontWeight: 700,
						                  textTransform: 'uppercase', letterSpacing: '0.06em',
						                  color: dsColorNew.appBlue, mb: 0.5 }}>
							Preview
						</Typography>
						<Typography sx={{ fontSize: 13, color: dsColorNew.textTitle,
						                  lineHeight: 1.5 }}>
							{preview}
						</Typography>
					</Box>

					{allValues.length > 0 && childOptions.every(o => o.disabled) && (
						<Typography sx={{ fontSize: 12.5, color: dsColorNew.dangerRed }}>
							No values left to inherit in <strong>{DIM_BY_CODE[dimension]?.label}</strong>
							{' '}— every one is already a parent or a child in another tether.
						</Typography>
					)}
				</Stack>
			</AppDialogBody>
			<AppDialogFooter>
				<AppDialogCancel onClick={onClose}/>
				<AppBulkEditAction disabled={!isValid} onClick={commit}>
					{isEdit ? 'Save changes' : 'Save tether'}
				</AppBulkEditAction>
			</AppDialogFooter>
		</AppDialog>
	)
}

const STALE_LINE = 'is no longer a bracket — pick one of the list.'
const staleChipSx = {
	bgcolor: dsColorNew.noteAmberBg, color: dsColorNew.noteAmber,
	border: '1px dashed', borderColor: dsColorNew.noteAmber,
}

// LOR options sit under "Brackets of your read pricelists"; a saved value
// that is no bracket any more is kept apart, first (criterion 14).
const groupOf = (dim) => (dim === 'lor'
	? (o) => (o.stale ? 'Saved, no longer a bracket' : 'Brackets of your read pricelists')
	: undefined)

/** One option: its label, and under it why it is disabled or that it already heads another tether. */
function OptionRow({ props, o }) {
	return (
		<li {...props} key={o.code} title={o.reason || o.note || undefined}>
			<Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
				<Box component="span" sx={{ fontWeight: 600, fontSize: 13,
				                            color: o.stale ? dsColorNew.noteAmber : dsColorNew.textTitle }}>
					{o.label}
				</Box>
				{o.reason && <Typography sx={{ fontSize: 12, color: dsColorNew.noteAmber }}>{o.reason}</Typography>}
				{!o.reason && o.note && <Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>{o.note}</Typography>}
			</Box>
		</li>
	)
}

function ChildrenPicker({ options, value, dim, loading, stale = [], onChange, placeholder }) {
	const selected = value.map(code => options.find(o => o.code === code) || { code, label: code, stale: true })
	const dimMeta = DIM_BY_CODE[dim]
	return (
		<Box>
			<Autocomplete multiple size="small"
			              options={options}
			              value={selected}
			              disabled={loading}
			              disableCloseOnSelect
			              groupBy={groupOf(dim)}
			              getOptionLabel={(o) => o.label}
			              getOptionDisabled={(o) => !!o.disabled}
			              isOptionEqualToValue={(a, b) => a.code === b.code}
			              onChange={(_, next) => onChange(next.map(o => o.code))}
			              renderTags={(list, getTagProps) =>
			                list.map((o, idx) => (
			                  <Chip {...getTagProps({ index: idx })}
			                        key={o.code} size="small"
			                        label={o.label}
			                        sx={{
			                            height: 22, fontWeight: 700, fontSize: 11.5,
			                            bgcolor: dimMeta?.palette.bg || dsColorNew.pillTrackBg,
			                            color:   dimMeta?.palette.fg || dsColorNew.textTitle,
			                            border: '1px solid',
			                            borderColor: dimMeta?.palette.border || dsColorNew.grayBorder,
			                            ...(stale.includes(o.code) ? staleChipSx : {}),
			                        }}/>
			                ))}
			              renderInput={(params) => (
			                  <TextField {...params}
			                             placeholder={loading ? 'Loading brackets…' : value.length ? '' : placeholder}
			                             sx={{ bgcolor: dsColorNew.white, ...inputSx }}/>
			              )}
			              renderOption={(props, o) => <OptionRow key={o.code} props={props} o={o}/>}/>
			{stale.length > 0 && (
				<Typography sx={{ fontSize: 12, color: dsColorNew.noteAmber, mt: 0.5 }}>
					{stale.join(', ')} {STALE_LINE}
				</Typography>
			)}
		</Box>
	)
}

function ValuePicker({ options, value, dim, loading, stale = false, onChange, placeholder }) {
	const selected = value ? (options.find(o => o.code === value) || { code: value, label: value, stale: true }) : null
	return (
		<Box>
			<Autocomplete size="small"
			              options={options}
			              value={selected}
			              disabled={loading}
			              groupBy={groupOf(dim)}
			              getOptionLabel={(o) => o.label}
			              getOptionDisabled={(o) => !!o.disabled}
			              isOptionEqualToValue={(a, b) => a.code === b.code}
			              onChange={(_, next) => onChange(next?.code ?? '')}
			              renderInput={(params) => (
			                  <TextField {...params} placeholder={loading ? 'Loading brackets…' : placeholder}
			                             sx={{ bgcolor: stale ? dsColorNew.noteAmberBg : dsColorNew.white, ...inputSx,
			                                   ...(stale ? { '& .MuiOutlinedInput-notchedOutline': { borderStyle: 'dashed', borderColor: dsColorNew.noteAmber } } : {}),
			                                   '& .MuiOutlinedInput-input': {
			                                       fontWeight: 700,
			                                       color: stale ? dsColorNew.noteAmber : undefined,
			                                   } }}/>
			              )}
			              renderOption={(props, o) => <OptionRow key={o.code} props={props} o={o}/>}/>
			{stale && (
				<Typography sx={{ fontSize: 12, color: dsColorNew.noteAmber, mt: 0.5 }}>
					{value} {STALE_LINE}
				</Typography>
			)}
			{!stale && selected?.note && (
				<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, mt: 0.5 }}>{selected.note}</Typography>
			)}
		</Box>
	)
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function DimensionChip({ dim }) {
	const d = DIM_BY_CODE[dim]
	if (!d) return <span>{dim}</span>
	return (
		<Chip size="small" label={d.short}
		      sx={{ height: 20, fontSize: 11, fontWeight: 700,
		            letterSpacing: '0.05em',
		            bgcolor: d.palette.bg, color: d.palette.fg,
		            border: '1px solid', borderColor: d.palette.border }}/>
	)
}

/**
 * Parent → children with the delta. A LOR value that is no bracket of any
 * read pricelist any more wears an amber dashed chip and the row says so
 * (criterion 14).
 */
function TetherArrow({ tether, stale = [] }) {
	const cat = useDimensionCatalog()
	const d = DIM_BY_CODE[tether.dimension]
	const chipSx = {
		height: 26, fontSize: 12, fontWeight: 700,
		bgcolor: dsColorNew.white,
		border: '1px solid',
		borderColor: d?.palette.border || dsColorNew.grayBorder,
		color: d?.palette.fg || dsColorNew.textTitle,
		fontFamily: d?.mono ? 'inherit' : 'inherit',
		'& .MuiChip-label': { px: 1 },
	}
	const sxOf = (v) => (stale.includes(String(v)) ? { ...chipSx, ...staleChipSx } : chipSx)
	const children = tether.children || []
	return (
		<Box>
		<Stack direction="row" gap={0.5} alignItems="center" flexWrap="wrap">
			<Chip size="small" label={cat.short(tether.dimension, tether.parent)} sx={sxOf(tether.parent)}/>
			<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
				<Box sx={{ height: 1, bgcolor: dsColorNew.grayBorder, width: 8 }}/>
				<Box sx={{ fontSize: 11, fontWeight: 700,
				           bgcolor: dsColorNew.pillTrackBg,
				           border: '1px solid', borderColor: dsColorNew.grayBorder,
				           borderRadius: 0.75, px: 0.75, py: 0.15,
				           color: tether.delta.v > 0 ? dsColorNew.successText
				                 : tether.delta.v < 0 ? dsColorNew.dangerRed : dsColorNew.textMuted,
				           fontFamily: 'inherit' }}>
					{formatDelta(tether.delta)}
				</Box>
				<Box sx={{ height: 1, bgcolor: dsColorNew.grayBorder, width: 8 }}/>
				<ArrowIcon sx={{ fontSize: 20, color: dsColorNew.textSubtle }}/>
			</Box>
			{children.length === 0 ? (
				<Chip size="small" label="—"
				      sx={{ ...chipSx, borderStyle: 'dashed', color: dsColorNew.textSubtle }}/>
			) : (
				<Stack direction="row" gap={0.35} flexWrap="wrap">
					{children.map(c => (
						<Chip key={c} size="small"
						      label={cat.short(tether.dimension, c)}
						      sx={sxOf(c)}/>
					))}
				</Stack>
			)}
		</Stack>
		{stale.length > 0 && (
			<Typography sx={{ fontSize: 12, color: dsColorNew.noteAmber, mt: 0.5 }}>
				{stale.join(', ')} {stale.length === 1 ? 'is' : 'are'} no longer a bracket of any read pricelist. Edit to change {stale.length === 1 ? 'it' : 'them'}
			</Typography>
		)}
		</Box>
	)
}

function DeltaChip({ delta }) {
	if (!delta || delta.v == null) {
		return <Typography sx={{ color: dsColorNew.textSubtle }}>—</Typography>
	}
	const isZero = delta.v === 0
	const up     = delta.v > 0
	return (
		<Chip size="small" label={formatDelta(delta)}
		      sx={{ height: 24, fontSize: 12.5, fontWeight: 700,
		            fontFamily: 'inherit',
		            bgcolor: isZero ? dsColorNew.pillTrackBg
		                   : up     ? dsColorNew.successBg : dsColorNew.dangerBg,
		            color:   isZero ? dsColorNew.textMuted
		                   : up     ? dsColorNew.successText : dsColorNew.dangerRed,
		            border: '1px solid',
		            borderColor: isZero ? dsColorNew.grayBorder
		                       : up     ? alpha(dsColorNew.successBar, 0.4) : dsColorNew.dangerBorder }}/>
	)
}

/**
 * Up or down, as a control instead of a character the operator has to
 * smuggle past a number input. The app's pill toggle; the delta chip
 * beside it still says dearer in green and cheaper in red.
 */
// Each sign keeps its sentence on hover and for screen readers, as the old buttons did
const DIRECTION_OPTIONS = [
	{ value: 'up', label: <span title="Child is dearer than the parent" aria-label="Child is dearer than the parent">+</span> },
	{ value: 'down', label: <span title="Child is cheaper than the parent" aria-label="Child is cheaper than the parent">−</span> },
]

function DirectionToggle({ value, onChange }) {
	return <AppPillToggle size="small" value={value} options={DIRECTION_OPTIONS} onChange={onChange}/>
}

const UNIT_OPTIONS = [{ value: 'pct', label: '%' }, { value: 'abs', label: '€' }]

function UnitToggle({ value, onChange }) {
	return <AppPillToggle size="small" value={value} options={UNIT_OPTIONS} onChange={onChange}/>
}

function Label({ children }) {
	return (
		<Typography sx={{ fontSize: 11, fontWeight: 700,
		                  textTransform: 'uppercase', letterSpacing: '0.06em',
		                  color: dsColorNew.textMuted, mb: 0.5 }}>
			{children}
		</Typography>
	)
}

function HeadCell({ children, ...rest }) {
	return (
		<TableCell {...rest}
		           sx={{ ...headCellSx, py: 1.25 }}>
			{children}
		</TableCell>
	)
}
